// PaymentDue.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "Pay Now" for a renewal that didn't go through — insufficient balance, an
// expired card, a bank blip.
//
// Today that failure ends with handlePaymentFailure deactivating the agent and
// a notification telling the customer to "update your payment details to restore
// service" — with nowhere in the app to actually do it. This banner is the
// missing half: it shows what is owed and charges the card on file.
//
// It pays the invoice Stripe ALREADY has open. Starting a fresh checkout would
// double-bill the moment Stripe's own scheduled retry lands on the same invoice.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from "react";

import { payInvoice } from "../../Store/apiStore";
import { stripePromise } from "../../lib/stripeClient";
import styles from "./PaymentDue.module.css";

const formatAmount = (amount, currency) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || ""} ${value.toFixed(2)}`.trim();
  }
};

const formatDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" });
};

const PaymentDue = ({ invoices = [], onPaid }) => {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [hostedUrl, setHostedUrl] = useState("");
  const [success, setSuccess] = useState("");

  // `open` is Stripe's word for "still owed". Drafts, paid, void and
  // uncollectible are all settled one way or another and must not prompt.
  const dueInvoices = (invoices || []).filter((inv) => inv.status === "open");

  if (!dueInvoices.length) return null;

  const handlePay = async (invoice) => {
    setBusyId(invoice.id);
    setError("");
    setHostedUrl("");
    setSuccess("");

    try {
      const result = await payInvoice(invoice.id);

      if (result?.status === "requires_action" && result?.clientSecret) {
        // The bank wants an OTP / 3DS challenge. Only the browser can run it,
        // so finish the same PaymentIntent here rather than failing the retry.
        const stripe = await stripePromise;
        if (!stripe) throw new Error("Payment couldn't be completed. Please contact support.");

        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmError) {
          throw new Error(
            confirmError.message || "Your bank didn't approve the payment. Please try again."
          );
        }
      }

      setSuccess("Payment received. Your plan is being restored.");
      // The webhook is what actually reactivates the agent, so the parent
      // refetches rather than this component guessing at the new state.
      await onPaid?.();
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
      // Set only when the server couldn't reach the PaymentIntent — Stripe's
      // hosted page can always take the money.
      if (err.hostedInvoiceUrl) setHostedUrl(err.hostedInvoiceUrl);
      if (err.needsPaymentMethod) {
        setError(`${err.message} You can add one in Payment Method below.`);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.container}>
      {dueInvoices.map((invoice) => {
        const isBusy = busyId === invoice.id;
        const amount = formatAmount(invoice.amountDue, invoice.currency);
        const date = formatDate(invoice.created);

        return (
          <div key={invoice.id} className={styles.banner}>
            <div className={styles.icon} aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 8v5M12 16.5h.01M10.3 3.9 2.4 17.4A2 2 0 0 0 4.1 20.4h15.8a2 2 0 0 0 1.7-3l-7.9-13.5a2 2 0 0 0-3.4 0Z"
                  stroke="#B54708"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className={styles.body}>
              <p className={styles.title}>
                {/* `attempted` is the difference between "we tried and your bank
                    said no" and "this bill just isn't paid yet". Telling someone
                    their payment failed when it was never attempted is alarming
                    and wrong. */}
                {invoice.attempted ? "Your last payment failed" : "You have a payment due"}
              </p>
              <p className={styles.detail}>
                {amount} {date ? `· ${date}` : ""}
                {invoice.attempted
                  ? " — your agent stays paused until this is paid."
                  : ""}
              </p>

              {error && <p className={styles.error}>{error}</p>}
              {success && <p className={styles.success}>{success}</p>}
              {hostedUrl && (
                <a
                  className={styles.hostedLink}
                  href={hostedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Pay on Stripe instead
                </a>
              )}
            </div>

            <button
              className={styles.payBtn}
              onClick={() => handlePay(invoice)}
              disabled={isBusy || !!success}
            >
              {isBusy ? "Paying…" : "Pay Now"}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default PaymentDue;
